import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Layers, TrendingUp, TrendingDown, Minus, BarChart3, Zap, Activity } from 'lucide-react'
import { GeometricAccordion } from '../ui/GeometricAccordion'
import { formatIndianScale } from '../../lib/table-utils'
import { getStrikeStepForSymbol } from '../../config/constants'
import { useSettingsStore } from '../../stores/settingsStore'
import { cn } from '../../lib/utils'

export interface CombinedStrikeAnalysisProps {
    symbol: string
    marketData?: any
    options?: any[]
    spotPrice?: number
    strikeGap?: number
    activeRange?: number
    includeAtm?: boolean
    onIncludeAtmChange?: (includeAtm: boolean) => void
    storageKey?: string
    defaultOpen?: boolean
    className?: string
}

interface RatioGaugeProps {
    belowVal: number
    aboveVal: number
    netVal: number
}

const CombinedRatioGauge: React.FC<RatioGaugeProps> = ({ belowVal, aboveVal, netVal }) => {
    const { belowPct, abovePct, belowText, aboveText, spreadText, isAboveDominant, isBelowDominant } = useMemo(() => {
        const b = Math.abs(belowVal || 0)
        const a = Math.abs(aboveVal || 0)
        const total = b + a

        if (total <= 0) {
            return {
                belowPct: 50,
                abovePct: 50,
                belowText: '50.0%',
                aboveText: '50.0%',
                spreadText: '0.0%',
                isAboveDominant: false,
                isBelowDominant: false,
            }
        }

        const rawB = (b / total) * 100
        const bp = Number(rawB.toFixed(1))
        const ap = Number((100 - bp).toFixed(1))
        const diff = Math.abs(ap - bp).toFixed(1)

        return {
            belowPct: Math.min(100, Math.max(0, bp)),
            abovePct: Math.min(100, Math.max(0, ap)),
            belowText: `${bp.toFixed(1)}%`,
            aboveText: `${ap.toFixed(1)}%`,
            spreadText: `${diff}%`,
            isAboveDominant: ap - bp >= 4,
            isBelowDominant: bp - ap >= 4,
        }
    }, [belowVal, aboveVal])

    return (
        <div
            className="flex flex-col gap-1.5 mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800/60 select-none"
            title={`Below: ${formatIndianScale(belowVal)} (${belowText}) | Above: ${formatIndianScale(aboveVal)} (${aboveText}) | Net: ${(netVal > 0 ? '+' : '') + formatIndianScale(netVal)}`}
        >
            {/* Top Proportional Readout */}
            <div className="flex items-center justify-between text-[9.5px] font-black px-0.5">
                <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]" />
                    <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Below</span>
                    <span className="tabular-nums text-[10.5px]">{belowText}</span>
                </div>

                <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400 whitespace-nowrap">
                    <span className="tabular-nums text-[10.5px]">{aboveText}</span>
                    <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Above</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.8)]" />
                </div>
            </div>

            {/* Precision Confluence Track with Sliding Boundary Needle & 50% Equilibrium Pin */}
            <div className="relative w-full py-0.5">
                {/* Track Capsule */}
                <div className="relative w-full h-2.5 bg-slate-100 dark:bg-slate-950 rounded-full overflow-hidden flex shadow-inner border border-slate-200/80 dark:border-slate-800/80 p-[1px]">
                    <div
                        className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-400 h-full rounded-l-full transition-all duration-500 ease-out shadow-[inset_0_1px_1px_rgba(255,255,255,0.3)]"
                        style={{ width: `${belowPct}%` }}
                    />
                    {/* Sliding Boundary Needle */}
                    <div className="w-[2px] h-full bg-white dark:bg-slate-100 shadow-[0_0_6px_rgba(255,255,255,0.9)] z-20 shrink-0 -mx-[1px]" />
                    <div
                        className="bg-gradient-to-r from-rose-400 via-rose-500 to-rose-600 h-full rounded-r-full transition-all duration-500 ease-out shadow-[inset_0_1px_1px_rgba(255,255,255,0.3)]"
                        style={{ width: `${abovePct}%` }}
                    />
                </div>

                {/* 50% Equilibrium Center Pin */}
                <div
                    className="absolute left-1/2 top-0 bottom-0 w-[1.5px] -ml-[0.75px] bg-slate-900/60 dark:bg-white/80 rounded-full z-10 pointer-events-none shadow-xs"
                    title="50% Equilibrium"
                />
            </div>

            {/* Dynamic Spread Dominance Pill */}
            <div className="px-0.5">
                {isAboveDominant ? (
                    <div className="flex items-center justify-between text-[8px] sm:text-[8.5px] font-black px-2 py-0.5 rounded-md bg-rose-500/10 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-300/60 dark:border-rose-500/30 shadow-2xs">
                        <span className="font-mono font-extrabold text-[8.5px]">+{spreadText} Spread</span>
                        <span className="tracking-wide">Above Dominant ▶</span>
                    </div>
                ) : isBelowDominant ? (
                    <div className="flex items-center justify-between text-[8px] sm:text-[8.5px] font-black px-2 py-0.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-500/30 shadow-2xs">
                        <span className="tracking-wide">◀ Below Dominant</span>
                        <span className="font-mono font-extrabold text-[8.5px]">+{spreadText} Spread</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center text-[8px] sm:text-[8.5px] font-black px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 border border-slate-200/80 dark:border-slate-700/80 shadow-2xs">
                        <span className="tracking-wide">Balanced Flow (±{spreadText})</span>
                    </div>
                )}
            </div>
        </div>
    )
}

interface CombinedCardProps {
    title: string
    subtitle: string
    accentColor: 'blue' | 'purple' | 'amber'
    belowVal: number
    aboveVal: number
    netVal: number
    effectiveIncludeAtm: boolean
    handleAtmToggle: () => void
    isChangeMetric?: boolean
}

const CombinedMetricCard: React.FC<CombinedCardProps> = ({
    title,
    subtitle,
    accentColor,
    belowVal,
    aboveVal,
    netVal,
    effectiveIncludeAtm,
    handleAtmToggle,
    isChangeMetric = false,
}) => {
    const { belowText, aboveText } = useMemo(() => {
        const b = Math.abs(belowVal || 0)
        const a = Math.abs(aboveVal || 0)
        const total = b + a
        if (total <= 0) return { belowText: '50.0%', aboveText: '50.0%' }
        const bp = Number(((b / total) * 100).toFixed(1))
        const ap = Number((100 - bp).toFixed(1))
        return {
            belowText: `${bp.toFixed(1)}%`,
            aboveText: `${ap.toFixed(1)}%`,
        }
    }, [belowVal, aboveVal])

    const themeStyles = useMemo(() => {
        switch (accentColor) {
            case 'blue':
                return {
                    accentLine: 'before:bg-gradient-to-r before:from-blue-500 before:via-indigo-500 before:to-cyan-400',
                    dotBg: 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.9)]',
                    glowDark: 'dark:shadow-[0_4px_24px_-4px_rgba(59,130,246,0.15)]',
                    ambientGlow: 'bg-gradient-to-b from-blue-500/[0.03] via-transparent to-transparent',
                }
            case 'purple':
                return {
                    accentLine: 'before:bg-gradient-to-r before:from-violet-500 before:via-purple-500 before:to-fuchsia-400',
                    dotBg: 'bg-violet-500 shadow-[0_0_8px_rgba(139,92,246,0.9)]',
                    glowDark: 'dark:shadow-[0_4px_24px_-4px_rgba(139,92,246,0.15)]',
                    ambientGlow: 'bg-gradient-to-b from-violet-500/[0.03] via-transparent to-transparent',
                }
            case 'amber':
                return {
                    accentLine: 'before:bg-gradient-to-r before:from-amber-500 before:via-orange-500 before:to-rose-400',
                    dotBg: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.9)]',
                    glowDark: 'dark:shadow-[0_4px_24px_-4px_rgba(245,158,11,0.15)]',
                    ambientGlow: 'bg-gradient-to-b from-amber-500/[0.03] via-transparent to-transparent',
                }
        }
    }, [accentColor])

    const belowColorClass = isChangeMetric
        ? (belowVal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
        : 'text-emerald-600 dark:text-emerald-400'

    const aboveColorClass = isChangeMetric
        ? (aboveVal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
        : 'text-rose-600 dark:text-rose-400'

    return (
        <div className={cn(
            "relative overflow-hidden col-span-1 bg-white/95 dark:bg-slate-900/90 backdrop-blur-xl rounded-2xl border border-slate-200/80 dark:border-slate-800/80 p-2.5 sm:p-3 xl:p-3.5 transition-all duration-300 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700/80 shadow-xs before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px]",
            themeStyles.accentLine,
            themeStyles.glowDark,
            themeStyles.ambientGlow
        )}>
            <div>
                {/* Header: Title on Left, Dedicated ATM Toggle Chip on Right (Zero Collision) */}
                <div className="flex items-center justify-between gap-1 pb-2 mb-2 sm:mb-2.5 border-b border-slate-100 dark:border-slate-800/80">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className={cn("w-2 h-2 rounded-full shrink-0", themeStyles.dotBg)} />
                        <span className="text-xs sm:text-[12.5px] font-black tracking-wide uppercase text-slate-900 dark:text-slate-100 whitespace-nowrap">
                            {title}
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAtmToggle()
                        }}
                        title={effectiveIncludeAtm ? "ATM strike is included (≤ATM). Click to toggle." : "ATM strike is excluded (<ATM). Click to toggle."}
                        className={cn(
                            "text-[8px] sm:text-[8.5px] font-black px-2 py-0.5 rounded-md shrink-0 transition-all cursor-pointer border select-none leading-none shadow-2xs whitespace-nowrap flex items-center gap-1",
                            effectiveIncludeAtm
                                ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-200 border-emerald-400/70 dark:border-emerald-500/50 hover:bg-emerald-500/25"
                                : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-300/80 dark:border-amber-600/60 hover:bg-amber-500/25"
                        )}
                    >
                        <span>ATM</span>
                        <span className="font-mono text-[9px]">{effectiveIncludeAtm ? '≤' : '<'}</span>
                    </button>
                </div>

                {/* Unified 3-Column Symmetrical Metric Panel (Divided by crisp vertical lines, Zero Overlap) */}
                <div className="grid grid-cols-3 divide-x divide-slate-200/60 dark:divide-slate-800 bg-slate-50/80 dark:bg-slate-950/50 rounded-xl py-2 px-0.5 border border-slate-200/60 dark:border-slate-800/80 shadow-2xs">
                    {/* Column 1: Below */}
                    <div className="flex flex-col items-center justify-between px-1 text-center min-w-0">
                        <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                            Below
                        </span>
                        <span className={cn(
                            "text-xs sm:text-[13px] xl:text-sm font-black tracking-tight tabular-nums block whitespace-nowrap my-0.5",
                            belowColorClass
                        )}>
                            {(isChangeMetric && belowVal > 0 ? '+' : '') + formatIndianScale(belowVal)}
                        </span>
                        <span className="text-[8.5px] sm:text-[9px] font-bold text-slate-400 dark:text-slate-500 tabular-nums whitespace-nowrap">
                            {belowText}
                        </span>
                    </div>

                    {/* Column 2: Above */}
                    <div className="flex flex-col items-center justify-between px-1 text-center min-w-0">
                        <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-rose-600 dark:text-rose-400 whitespace-nowrap">
                            Above
                        </span>
                        <span className={cn(
                            "text-xs sm:text-[13px] xl:text-sm font-black tracking-tight tabular-nums block whitespace-nowrap my-0.5",
                            aboveColorClass
                        )}>
                            {(isChangeMetric && aboveVal > 0 ? '+' : '') + formatIndianScale(aboveVal)}
                        </span>
                        <span className="text-[8.5px] sm:text-[9px] font-bold text-slate-400 dark:text-slate-500 tabular-nums whitespace-nowrap">
                            {aboveText}
                        </span>
                    </div>

                    {/* Column 3: Net Diff */}
                    <div className="flex flex-col items-center justify-between px-1 text-center min-w-0">
                        <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            Net Diff
                        </span>
                        <span className={cn(
                            "text-xs sm:text-[13px] xl:text-sm font-black tracking-tight tabular-nums block whitespace-nowrap my-0.5",
                            netVal > 0 ? 'text-emerald-600 dark:text-emerald-400' : netVal < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'
                        )}>
                            {(netVal > 0 ? '+' : '') + formatIndianScale(netVal)}
                        </span>
                        <span className={cn(
                            "text-[8px] sm:text-[8.5px] font-black whitespace-nowrap",
                            netVal > 0 ? 'text-emerald-600 dark:text-emerald-400' : netVal < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500'
                        )}>
                            {netVal > 0 ? 'Below Heavy' : netVal < 0 ? 'Above Heavy' : 'Neutral'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Confluence Spectrum Gauge */}
            <CombinedRatioGauge belowVal={belowVal} aboveVal={aboveVal} netVal={netVal} />
        </div>
    )
}

export const CombinedStrikeAnalysis: React.FC<CombinedStrikeAnalysisProps> = ({
    symbol,
    marketData,
    options: propOptions,
    spotPrice: propSpotPrice,
    strikeGap: propStrikeGap,
    activeRange: propActiveRange,
    includeAtm: propIncludeAtm,
    onIncludeAtmChange,
    storageKey = 'home_accordion_combined',
    defaultOpen = true,
    className = '',
}) => {
    const settingsRange = useSettingsStore((s) => s.range)
    const effectiveRange = propActiveRange || settingsRange || 10
    const step = propStrikeGap || getStrikeStepForSymbol(symbol)

    // Manage internal/external ATM toggle state with localStorage persistence
    const [internalIncludeAtm, setInternalIncludeAtm] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storageKey}_includeAtm`)
            if (saved !== null) return saved === 'true'
        }
        return propIncludeAtm !== undefined ? propIncludeAtm : true
    })

    useEffect(() => {
        if (propIncludeAtm !== undefined) {
            setInternalIncludeAtm(propIncludeAtm)
        }
    }, [propIncludeAtm])

    const effectiveIncludeAtm = propIncludeAtm !== undefined ? propIncludeAtm : internalIncludeAtm

    const handleAtmToggle = useCallback(() => {
        const next = !effectiveIncludeAtm
        setInternalIncludeAtm(next)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storageKey}_includeAtm`, next.toString())
        }
        onIncludeAtmChange?.(next)
    }, [effectiveIncludeAtm, storageKey, onIncludeAtmChange])

    const metrics = useMemo(() => {
        const rawOptions = propOptions || marketData?.options || marketData?.rawOpts || []
        if (!rawOptions.length) {
            return {
                belowCombOI: 0,
                aboveCombOI: 0,
                netCombOI: 0,
                belowCombVol: 0,
                aboveCombVol: 0,
                netCombVol: 0,
                belowCombOIChg: 0,
                aboveCombOIChg: 0,
                netCombOIChg: 0,
            }
        }

        let indexLtp = propSpotPrice || marketData?.spotPrice || marketData?.underlyingValue || marketData?.indexLtp || 0
        let atmStrike = marketData?.atmStrike || 0
        if (atmStrike <= 0 && indexLtp > 0) {
            atmStrike = Math.round(indexLtp / step) * step
        }
        if (atmStrike <= 0 && rawOptions.length > 0) {
            atmStrike = rawOptions[Math.floor(rawOptions.length / 2)]?.strike || rawOptions[0]?.strike || 0
        }

        const minStrike = atmStrike > 0 ? atmStrike - effectiveRange * step : 0
        const maxStrike = atmStrike > 0 ? atmStrike + effectiveRange * step : Infinity

        let aboveCombOI = 0
        let aboveCombVol = 0
        let aboveCombOIChg = 0

        let belowCombOI = 0
        let belowCombVol = 0
        let belowCombOIChg = 0

        rawOptions.forEach((opt: any) => {
            const strike = Number(opt.strike || opt.strikePrice || 0)
            if (atmStrike > 0 && (strike < minStrike || strike > maxStrike)) return

            const ceOI = Number(opt.callOpenInterest || opt.ce?.oi || opt.ce?.openInterest || (opt.type === 'CE' ? opt.oi : 0) || 0)
            const peOI = Number(opt.putOpenInterest || opt.pe?.oi || opt.pe?.openInterest || (opt.type === 'PE' ? opt.oi : 0) || 0)
            const combOI = ceOI + peOI

            const ceVol = Number(opt.callVolume || opt.ce?.volume || opt.ce?.vol || (opt.type === 'CE' ? opt.volume : 0) || 0)
            const peVol = Number(opt.putVolume || opt.pe?.volume || opt.pe?.vol || (opt.type === 'PE' ? opt.volume : 0) || 0)
            const combVol = ceVol + peVol

            const ceOIChg = Number(opt.callOpenInterestChange || opt.callChange || opt.ce?.oiChange || (opt.type === 'CE' ? opt.oiChg : 0) || 0)
            const peOIChg = Number(opt.putOpenInterestChange || opt.putChange || opt.pe?.oiChange || (opt.type === 'PE' ? opt.oiChg : 0) || 0)
            const combOIChg = ceOIChg + peOIChg

            if (strike > atmStrike) {
                aboveCombOI += combOI
                aboveCombVol += combVol
                aboveCombOIChg += combOIChg
            } else if (strike < atmStrike) {
                belowCombOI += combOI
                belowCombVol += combVol
                belowCombOIChg += combOIChg
            } else if (strike === atmStrike) {
                // When ATM is ON: add ATM strike to below calculations; when OFF: completely excluded
                if (effectiveIncludeAtm) {
                    belowCombOI += combOI
                    belowCombVol += combVol
                    belowCombOIChg += combOIChg
                }
            }
        })

        return {
            belowCombOI,
            aboveCombOI,
            netCombOI: belowCombOI - aboveCombOI,
            belowCombVol,
            aboveCombVol,
            netCombVol: belowCombVol - aboveCombVol,
            belowCombOIChg,
            aboveCombOIChg,
            netCombOIChg: belowCombOIChg - aboveCombOIChg,
        }
    }, [propOptions, marketData, propSpotPrice, step, effectiveRange, effectiveIncludeAtm])

    return (
        <div className={className}>
            <GeometricAccordion
                title="Combined Strike Analysis"
                subtitle={`ATM- VS ATM+ CUMULATIVE CONFLUENCE • ±${effectiveRange} STRIKES`}
                badge={<Layers size={18} />}
                storageKey={storageKey}
                defaultOpen={defaultOpen}
                headerRight={
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        {/* ATM ON/OFF Toggle Button with hardware-style LED switch */}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleAtmToggle()
                            }}
                            className={cn(
                                "px-2.5 py-1 text-[11px] sm:text-xs font-black rounded-lg transition-all flex items-center gap-1.5 shadow-2xs border cursor-pointer select-none",
                                effectiveIncludeAtm
                                    ? "bg-purple-500/15 dark:bg-purple-500/25 text-purple-700 dark:text-purple-300 border-purple-300/90 dark:border-purple-600/70 hover:bg-purple-500/25 shadow-purple-500/10"
                                    : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-200/60 dark:hover:bg-slate-700"
                            )}
                            title={effectiveIncludeAtm ? "ATM Included in Below strikes calculation (Click to toggle)" : "ATM Excluded from calculations (Click to toggle)"}
                        >
                            <span className={cn("w-1.5 h-1.5 rounded-full transition-all", effectiveIncludeAtm ? "bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.9)] animate-pulse" : "bg-slate-400")} />
                            <span>ATM {effectiveIncludeAtm ? 'ON' : 'OFF'}</span>
                        </button>

                        <div className="hidden lg:flex items-center gap-1.5 sm:gap-2">
                            <span className={cn(
                                "text-[10.5px] sm:text-[11px] font-black tabular-nums px-2 py-0.5 rounded-lg border shadow-2xs flex items-center gap-1",
                                metrics.netCombOI > 0 ? 'text-emerald-600 border-emerald-200/80 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30' : metrics.netCombOI < 0 ? 'text-rose-600 border-rose-200/80 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/30' : 'text-slate-500 border-slate-200 bg-slate-50 dark:bg-slate-800/80'
                            )}>
                                <span className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase">OI:</span>
                                {(metrics.netCombOI > 0 ? '+' : '') + formatIndianScale(metrics.netCombOI)}
                            </span>
                            <span className={cn(
                                "text-[10.5px] sm:text-[11px] font-black tabular-nums px-2 py-0.5 rounded-lg border shadow-2xs flex items-center gap-1",
                                metrics.netCombVol > 0 ? 'text-emerald-600 border-emerald-200/80 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30' : metrics.netCombVol < 0 ? 'text-rose-600 border-rose-200/80 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/30' : 'text-slate-500 border-slate-200 bg-slate-50 dark:bg-slate-800/80'
                            )}>
                                <span className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase">Vol:</span>
                                {(metrics.netCombVol > 0 ? '+' : '') + formatIndianScale(metrics.netCombVol)}
                            </span>
                            <span className={cn(
                                "text-[10.5px] sm:text-[11px] font-black tabular-nums px-2 py-0.5 rounded-lg border shadow-2xs flex items-center gap-1",
                                metrics.netCombOIChg > 0 ? 'text-emerald-600 border-emerald-200/80 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30' : metrics.netCombOIChg < 0 ? 'text-rose-600 border-rose-200/80 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/30' : 'text-slate-500 border-slate-200 bg-slate-50 dark:bg-slate-800/80'
                            )}>
                                <span className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase">Chg:</span>
                                {(metrics.netCombOIChg > 0 ? '+' : '') + formatIndianScale(metrics.netCombOIChg)}
                            </span>
                        </div>
                    </div>
                }
            >
                <div className="pt-1.5 sm:pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 lg:gap-3 xl:gap-3.5">
                        {/* Card 1: Combined OI */}
                        <CombinedMetricCard
                            title="Combined OI"
                            subtitle="Total Open Interest"
                            accentColor="blue"
                            belowVal={metrics.belowCombOI}
                            aboveVal={metrics.aboveCombOI}
                            netVal={metrics.netCombOI}
                            effectiveIncludeAtm={effectiveIncludeAtm}
                            handleAtmToggle={handleAtmToggle}
                        />

                        {/* Card 2: Combined Volume */}
                        <CombinedMetricCard
                            title="Combined Volume"
                            subtitle="Total Contracts Traded"
                            accentColor="purple"
                            belowVal={metrics.belowCombVol}
                            aboveVal={metrics.aboveCombVol}
                            netVal={metrics.netCombVol}
                            effectiveIncludeAtm={effectiveIncludeAtm}
                            handleAtmToggle={handleAtmToggle}
                        />

                        {/* Card 3: Combined OI Chg */}
                        <CombinedMetricCard
                            title="Combined OI Chg"
                            subtitle="Intraday Position Delta"
                            accentColor="amber"
                            belowVal={metrics.belowCombOIChg}
                            aboveVal={metrics.aboveCombOIChg}
                            netVal={metrics.netCombOIChg}
                            effectiveIncludeAtm={effectiveIncludeAtm}
                            handleAtmToggle={handleAtmToggle}
                            isChangeMetric={true}
                        />
                    </div>
                </div>
            </GeometricAccordion>
        </div>
    )
}

